<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import {
    type BlankRegistration,
    type ChampionshipRegistrationTeamMember,
    useChampionshipRegistrationSelectTeamData,
    validateTeamName,
} from "@/pages/Tasks/Tasks/championship-registration"
import ChampionshipRegistrationBlankRegistration from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationBlankRegistration.vue"
import ChampionshipRegistrationCancelMembersContent from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationCancelMembersContent.vue"
import ChampionshipRegistrationCancelTeamRequestContent from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationCancelTeamRequestContent.vue"
import ChampionshipRegistrationContactPersonForm from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationContactPersonForm.vue"
import ChampionshipRegistrationEditCategoryWizard from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationEditCategoryWizard"
import ChampionshipRegistrationEditTeam from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationEditTeam.vue"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { AnimateIf, injectMandatory, useHttpClient, useInlineTable, useLoadingState, useToggleableValue } from "@/vf"
import { computed, defineComponent, h, ref } from "vue"
import { useI18n } from "vue-i18n"
import { TaskViewMode } from "../../Issue/util"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
}>()

const data = useChampionshipRegistrationSelectTeamData()

type EditTeamWizardOption =
    | "safetyChampion"
    | "accuscore"
    | "category"
    | "members"
    | "teamName"
    | "membersCount"
    | "cancelMembers"
    | "cancelTeam"
const [whatToDo, setWhatToDo] = useToggleableValue<EditTeamWizardOption>(null)
const http = useHttpClient()
const inlineTable = useInlineTable(false)
const { t } = useI18n()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => viewMode),
)

/*─────────────────────────────────────┐
│  contact person                      │
└─────────────────────────────────────*/
const safetyChampion = ref(props.team.safetyChampionData)
const accuscoreMember = ref(props.team.accuscoreMemberData)

const safetyChampionSaving = useLoadingState()
const accuscoreMemberSaving = useLoadingState()

async function saveSafetyChampion() {
    await safetyChampionSaving.while(async () => {
        await http.put(
            `/championship/registration/${props.registrationId}/team/${props.team.id}/contact-person?viewMode=${viewMode}`,
            {
                which: "safetyChampion",
                memberId: safetyChampion.value?.id,
            },
        )
        inlineTable.close({ saved: true })
    })
}

async function saveAccuscoreMember() {
    await accuscoreMemberSaving.while(async () => {
        await http.put(
            `/championship/registration/${props.registrationId}/team/${props.team.id}/contact-person?viewMode=${viewMode}`,
            {
                which: "accuscore",
                memberId: accuscoreMember.value?.id,
            },
        )
        inlineTable.close({ saved: true })
    })
}

/*─────────────────────────────────────┐
│  edit name                           │
└─────────────────────────────────────*/
const teamName = ref<string>(props.team.teamName ?? "")
const teamNameValid = computed(() => validateTeamName(teamName.value))

async function saveTeamName() {
    if (!teamNameValid.value) {
        return
    }

    await http.put(
        `/championship/registration/${props.registrationId}/team/${props.team.id}/team-name?viewMode=${viewMode}`,
        {
            teamName: teamName.value,
        },
    )
    inlineTable.close({ saved: true })
}

/*─────────────────────────────────────┐
│  edit category                       │
└─────────────────────────────────────*/
// Check if the team's category requires accuscore
const teamCategoryRequiresAccuscore = computed(() => {
    const category = data.value?.categories.find(c => c.id === props.team.championshipCategory?.id)
    return category?.isEnableAccuscore ?? false
})

/*─────────────────────────────────────┐
│  edit members                        │
└─────────────────────────────────────*/
const membersSubmitting = ref(false)
const members = ref<ChampionshipRegistrationTeamMember[]>(
    props.team.members!.map(member => ({
        memberId: member.memberId!,
        type: member.type! as ChampionshipRegistrationTeamMember["type"],
    })),
)

async function saveMembers() {
    membersSubmitting.value = true
    try {
        await http.put(
            `/championship/registration/${props.registrationId}/team/${props.team.id}?viewMode=${viewMode}`,
            {
                members: members.value,
                teamName: props.team.teamName,
            },
        )
        inlineTable.close({ saved: true })
    } finally {
        membersSubmitting.value = false
    }
}

/*─────────────────────────────────────┐
│  cancel members                      │
└─────────────────────────────────────*/
const blankSlotCount = computed(
    () =>
        (props.team.numberOfParticipants ?? 0) -
        (props.team.numberOfNamedParticipants ?? 0) +
        (props.team.numberOfAlternates ?? 0) -
        (props.team.numberOfNamedAlternates ?? 0) +
        (props.team.numberOfEntourages ?? 0) -
        (props.team.numberOfNamedEntourages ?? 0),
)

const trans = defineTranslations({
    cancel_members: {
        de: "Personen stornieren",
        en: "Cancel persons",
    },
    cancel_members_description: {
        de: "{persons} Personen und {blankSlots} freie Plätze können storniert werden",
        en: "{persons} persons and {blankSlots} blank slots can be cancelled",
    },
})

/*─────────────────────────────────────┐
│  edit members count                  │
└─────────────────────────────────────*/
const teamMembersCount = ref<BlankRegistration>({
    numberOfParticipants: props.team.numberOfParticipants ?? 0,
    numberOfAlternates: props.team.numberOfAlternates ?? 0,
    numberOfEntourages: props.team.numberOfEntourages ?? 0,
})

async function saveEditMembersCount() {
    membersSubmitting.value = true
    try {
        await http.put(
            `/championship/registration/${props.registrationId}/team/${props.team.id}/members-count?viewMode=${viewMode}`,
            {
                numberOfParticipants: teamMembersCount.value.numberOfParticipants,
                numberOfAlternates: teamMembersCount.value.numberOfAlternates,
                numberOfEntourages: teamMembersCount.value.numberOfEntourages,
            },
        )
        inlineTable.close({ saved: true })
    } finally {
        membersSubmitting.value = false
    }
}

const renderMemberCount = defineComponent<{ team: ChampionshipRegistrationTeam }>({
    props: ["team"],
    setup(props) {
        return () =>
            h("span", [
                t(
                    "@tasks:tasks.championship_registration.team_list.number_of_participants",
                    props.team.numberOfParticipants ?? 0,
                    {
                        named: {
                            total: props.team.numberOfParticipants,
                            named: props.team.numberOfNamedParticipants,
                        },
                    },
                ),
                " ",
                t(
                    "@tasks:tasks.championship_registration.team_list.number_of_alternates",
                    props.team.numberOfAlternates ?? 0,
                    {
                        named: {
                            total: props.team.numberOfAlternates,
                            named: props.team.numberOfNamedAlternates,
                        },
                    },
                ),
                " ",
                t(
                    "@tasks:tasks.championship_registration.team_list.number_of_entourages",
                    props.team.numberOfEntourages ?? 0,
                    {
                        named: {
                            total: props.team.numberOfEntourages,
                            named: props.team.numberOfNamedEntourages,
                        },
                    },
                ),
            ])
    },
})
</script>

<template>
    <div>
        <b class="mb-3">{{ $t("@tasks:tasks.championship_registration.edit_team_wizard.what_to_do") }}</b>
        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('safetyChampion')"
            :class="{ dark: whatToDo === 'safetyChampion' }"
            :disabled="!policy.ChangeTeamContactPerson"
        >
            <b>{{ $t("@tasks:tasks.championship_registration.edit_team_wizard.edit_safety_champion") }}</b>
            <span v-if="props.team.safetyChampionData">
                {{ props.team.safetyChampionData?.name }}, {{ props.team.safetyChampionData?.email }}
            </span>
            <span v-else>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.no_safety_champion_yet") }}
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'safetyChampion'">
            <form @submit.prevent="saveSafetyChampion()" class="px-2">
                <div class="alert alert-info my-3">
                    {{ $t("@tasks:tasks.championship_registration.safety_champion_info") }}
                </div>
                <ChampionshipRegistrationContactPersonForm
                    v-model="safetyChampion"
                    :label="$t('@championship:registration.window.edit_team.contactPerson.select_safety_champion')"
                />
                <div
                    class="alert alert-warning d-flex align-items-center mt-4 mb-0"
                    v-if="data?.availableContactPersons?.length"
                >
                    <i class="fas fa-triangle-exclamation fa-lg mr-3"></i>
                    <b>{{ $t("@tasks:tasks.championship_registration.contact_person_must_be_registered_hint") }}</b>
                </div>

                <LoadingButton class="btn btn-primary mt-4" type="submit" :loading="safetyChampionSaving">
                    {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                </LoadingButton>
            </form>
        </AnimateIf>

        <template v-if="teamCategoryRequiresAccuscore">
            <button
                class="wizard-button font-weight-normal"
                @click="setWhatToDo('accuscore')"
                :class="{ dark: whatToDo === 'accuscore' }"
                :disabled="!policy.ChangeTeamContactPerson"
            >
                <b>{{ $t("@tasks:tasks.championship_registration.edit_team_wizard.edit_accuscore_member") }}</b>
                <span v-if="props.team.accuscoreMemberData">
                    {{ props.team.accuscoreMemberData?.name }}, {{ props.team.accuscoreMemberData?.email }}
                </span>
                <span v-else>
                    {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.no_accuscore_member_yet") }}
                </span>
            </button>
            <AnimateIf :if="whatToDo === 'accuscore'">
                <form @submit.prevent="saveAccuscoreMember()" class="px-2">
                    <div class="alert alert-info my-3">
                        {{ $t("@tasks:tasks.championship_registration.accuscore_member_info") }}
                    </div>
                    <ChampionshipRegistrationContactPersonForm
                        v-model="accuscoreMember"
                        :label="$t('@championship:registration.window.edit_team.contactPerson.select_accuscore_member')"
                    />
                    <div
                        class="alert alert-warning d-flex align-items-center mt-4 mb-0"
                        v-if="data?.availableContactPersons?.length"
                    >
                        <i class="fas fa-triangle-exclamation fa-lg mr-3"></i>
                        <b>{{ $t("@tasks:tasks.championship_registration.contact_person_must_be_registered_hint") }}</b>
                    </div>

                    <LoadingButton class="btn btn-primary mt-4" type="submit" :loading="accuscoreMemberSaving">
                        {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                    </LoadingButton>
                </form>
            </AnimateIf>
        </template>

        <!-- Each button in the wizard has its own policy check -->
        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('category')"
            :class="{ dark: whatToDo === 'category' }"
            :disabled="
                !policy.ChangeRequestCreateCategory &&
                !policy.ChangeTeamCategory &&
                !policy.ChangeRequestCreateCategoryWithMembers &&
                !policy.ChangeTeamCategoryWithMembers
            "
        >
            <b>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.edit_category") }}
            </b>
            <span>
                {{ props.team.championshipCategory?.name }}
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'category'">
            <div class="px-4 pb-4">
                <ChampionshipRegistrationEditCategoryWizard
                    :registration-id="registrationId"
                    :team="team"
                    @save="inlineTable.close({ saved: true })"
                />
            </div>
        </AnimateIf>

        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('membersCount')"
            :class="{ dark: whatToDo === 'membersCount' }"
            :disabled="!policy.UpdateTeamMembersCount"
        >
            <b>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.edit_members_count") }}
            </b>
            <span>
                <component :is="renderMemberCount" :team="props.team" />
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'membersCount'">
            <div class="px-2">
                <ChampionshipRegistrationBlankRegistration
                    :data="data"
                    :category="props.team.championshipCategory!"
                    :submitting="membersSubmitting"
                    v-model="teamMembersCount"
                    @save="saveEditMembersCount"
                />
            </div>
        </AnimateIf>

        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('members')"
            :class="{ dark: whatToDo === 'members' }"
            :disabled="!policy.SelectTeamUpdateTeam"
        >
            <b>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.edit_members") }}
            </b>
            <span>
                <component :is="renderMemberCount" :team="props.team" />
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'members'">
            <div class="px-2">
                <ChampionshipRegistrationEditTeam
                    :category-id="props.team.championshipCategory!.id!"
                    :submitting="membersSubmitting"
                    :team="props.team"
                    v-model="members"
                    @save="saveMembers"
                />
            </div>
        </AnimateIf>

        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('teamName')"
            :class="{ dark: whatToDo === 'teamName' }"
            :disabled="!policy.ChangeTeamName"
        >
            <b>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.edit_team_name") }}
            </b>
            <span>
                {{ props.team.teamName }}
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'teamName'">
            <form @submit.prevent="saveTeamName()" class="px-2">
                <div class="mb-3">
                    <b>
                        {{ $t("@tasks:tasks.championship_registration.wizard.please_enter_name") }}
                    </b>
                </div>
                <div class="my-3">
                    <b>{{ $t("@tasks:tasks.championship_registration.wizard.team_name") }}</b>
                    <input type="text" class="form-control" v-model="teamName" />
                </div>

                <button class="btn btn-primary mt-4" type="submit" :disabled="!teamNameValid">
                    {{ $t("@tasks:tasks.championship_registration.wizard.continue") }}
                </button>
            </form>
        </AnimateIf>

        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('cancelMembers')"
            :class="{ dark: whatToDo === 'cancelMembers' }"
            :disabled="!policy.CancelTeamMembers"
        >
            <b>{{ trans("cancel_members") }}</b>
            <span>
                {{
                    trans("cancel_members_description", {
                        persons: props.team.members?.length ?? 0,
                        blankSlots: blankSlotCount,
                    })
                }}
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'cancelMembers'">
            <div class="px-2">
                <ChampionshipRegistrationCancelMembersContent
                    :registration-id="props.registrationId"
                    :team="props.team"
                    @updated="inlineTable.close({ saved: true })"
                />
            </div>
        </AnimateIf>

        <button
            class="wizard-button font-weight-normal"
            @click="setWhatToDo('cancelTeam')"
            :class="{ dark: whatToDo === 'cancelTeam' }"
            :disabled="!policy.CancelTeamRequest || props.team.approvalState === 'canceled'"
        >
            <b>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.cancel_team") }}
            </b>
            <span v-if="props.team.approvalState === 'canceled'">
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.team_already_canceled") }}
            </span>
            <span v-else>
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.cancel_team_description") }}
            </span>
        </button>
        <AnimateIf :if="whatToDo === 'cancelTeam'">
            <div class="px-2">
                <ChampionshipRegistrationCancelTeamRequestContent
                    :registration-id="props.registrationId"
                    :team="props.team"
                    @updated="inlineTable.close({ saved: true })"
                />
            </div>
        </AnimateIf>
    </div>
</template>
